import type { DungeonMap, Position } from "../types/grid.js";
import type { SpawnerMetadata } from "../types/tiled.js";

/**
 * Spawner runtime (E10).
 *
 * Walks a loaded `DungeonMap` once at map-boot time and collects every
 * `spawner` object into an in-memory registry keyed by `trigger_id`.
 * When an Ink script (or any other system) fires a trigger — by calling
 * `WorldStore.fireTrigger(id)` — the main boot code consults
 * `resolveTrigger(id)` to get back the monster list and anchor cell
 * to instantiate.
 *
 * The registry does not mutate the world, does not touch spells or
 * sprites, and has no dependency on React or the EventBus. It is a pure
 * lookup table so main.tsx owns the actual `createMonster` call.
 */

export interface SpawnEntry {
  /** Grid cell the spawner was authored at — monsters spawn here. */
  readonly position: Position;
  /** SRD monster slugs to instantiate, in authored order. */
  readonly monster_ids: readonly string[];
  /** Optional faction override applied to every spawned enemy. */
  readonly faction?: string;
}

export class SpawnerRegistry {
  private readonly byTrigger = new Map<string, SpawnEntry>();

  /** Empty registry — useful for tests that seed manually. */
  constructor() {}

  /**
   * Scan `map.cells` once and register every `spawner` metadata
   * record. Later calls to `registerFromMap` are additive — multiple
   * maps' spawners can coexist (callers are expected to `clear()` on
   * floor change).
   */
  registerFromMap(map: DungeonMap): void {
    for (const cell of map.cells) {
      const m = cell.metadata;
      if (m?.type !== "spawner") continue;
      this.register(m, cell.position);
    }
  }

  register(meta: SpawnerMetadata, position: Position): void {
    const entry: SpawnEntry = {
      position,
      monster_ids: meta.monster_ids,
      ...(meta.faction !== undefined && { faction: meta.faction }),
    };
    this.byTrigger.set(meta.trigger_id, entry);
  }

  /**
   * Look up the spawn entry bound to `triggerId`. Returns `null` when
   * no spawner is bound (the trigger may still have Ink side-effects;
   * it just has no spawner attached).
   */
  resolveTrigger(triggerId: string): SpawnEntry | null {
    return this.byTrigger.get(triggerId) ?? null;
  }

  /** True if any spawner is bound to `triggerId`. */
  has(triggerId: string): boolean {
    return this.byTrigger.has(triggerId);
  }

  /** All registered trigger IDs (ordered by insertion). */
  triggerIds(): readonly string[] {
    return [...this.byTrigger.keys()];
  }

  clear(): void {
    this.byTrigger.clear();
  }
}

/** Singleton used by the boot code; tests should construct their own. */
export const spawnerRegistry = new SpawnerRegistry();
